import type { AutocompleteGameCardsRequest } from "modules/cards/model/card";

/** Two searches where one is a prefix of the other: the results are a superset
 *  or a subset of each other, so rows already on screen still belong under the
 *  new one. Anything else is a different name and starts cold. */
export function isPrefixRefinement(a: string, b: string): boolean {
  return a.startsWith(b) || b.startsWith(a);
}

// Everything that narrows the result set EXCEPT the name and the page. A hold
// is only sound when the held rows are a superset or subset of what is coming,
// and that is a claim about the name alone — a set, format, type or rarity
// change rewrites the result set under an UNCHANGED name, where the prefix test
// is trivially true. That let the deck Add Card box keep the unfiltered rows
// after a set was picked (setCode), after the location changed (types) and per
// deck format, with isPlaceholderData suppressing the pending state, so a
// printing from the set the user had just filtered away stayed selectable.
// Compared as a serialised shape rather than a field list so a field added to
// AutocompleteGameCardsRequest later is cold by default instead of silently
// held.
function filterShape(req: AutocompleteGameCardsRequest): string {
  const { string: _string, page: _page, ...filters } = req;
  return JSON.stringify(filters, Object.keys(filters).sort());
}

// Whether the rows of `held` (the last autocomplete request that had data)
// may stay on screen while `next` fetches. Only for a REFINED search — same
// filters, one string a prefix of the other — so the rows are a superset or
// subset of what is coming; a different name starts cold. Two guards on top:
//
// - `next` must be three characters or more: under that the query is disabled
//   and "" is a prefix of everything, so while the debounce lags behind a
//   cleared box the held rows would mount into the grid the moment the raw
//   input reaches three characters.
// - `wentCold`: no key has produced real data since the box last sat under
//   three characters. TanStack hands over the last query WITH data, skipping
//   the disabled key in between, but the grid unmounted at that key and is
//   showing its skeleton — there is nothing on screen to hold, and the held
//   rows would flash between the skeleton and the real rows. It has to be
//   "since", not "the previous key": typing a second key while the first is
//   still fetching still finds the pre-gap search as the last one with data.
export function holdsAutocompleteRows(
  held: AutocompleteGameCardsRequest | undefined,
  next: AutocompleteGameCardsRequest,
  wentCold: boolean
): boolean {
  return (
    held !== undefined &&
    !wentCold &&
    next.string.length >= 3 &&
    filterShape(held) === filterShape(next) &&
    isPrefixRefinement(next.string, held.string)
  );
}
